import React, { useEffect, useState } from 'react';
import { useAuth } from './ChatAuth';
import { Select } from './Select';
import { DateTimePicker } from './DateTimePicker';
import { SOUND_OUT_KEY, applySoundSink, forwardingAddress, getPref, soundSinkId, usePrefSetter } from './prefs';
import { Icon } from './icons';
import { pushState, enablePush, disablePush, PushState } from './push';

export type PrefCat =
  | 'notifications'
  | 'navigation'
  | 'home'
  | 'appearance'
  | 'messages'
  | 'language'
  | 'accessibility'
  | 'mark_read'
  | 'av'
  | 'privacy'
  | 'advanced';

const CATS: { key: PrefCat; label: string; icon: string }[] = [
  { key: 'notifications', label: 'Notifications', icon: 'bell' },
  { key: 'navigation', label: 'Navigation', icon: 'home' },
  { key: 'home', label: 'Home', icon: 'hash' },
  { key: 'appearance', label: 'Appearance', icon: 'moon' },
  { key: 'messages', label: 'Messages & media', icon: 'dm' },
  { key: 'language', label: 'Language & region', icon: 'clock' },
  { key: 'accessibility', label: 'Accessibility', icon: 'smiley' },
  { key: 'mark_read', label: 'Mark as read', icon: 'files' },
  { key: 'av', label: 'Audio & video', icon: 'phone' },
  { key: 'privacy', label: 'Privacy & visibility', icon: 'lock' },
  { key: 'advanced', label: 'Advanced', icon: 'gear' },
];

const THEMES = ['aubergine', 'midnight', 'ochre', 'forest', 'crimson', 'lagoon'];

export function Preferences({ initialCat, onClose }: { initialCat?: PrefCat; onClose: () => void }) {
  const { user, patchUser } = useAuth();
  const [cat, setCat] = useState<PrefCat>(initialCat || 'notifications');
  const set = usePrefSetter(user, patchUser);
  // Mobile is a drill-down: the category list, then a category's detail with a back
  // button. On desktop both panes always show (CSS ignores these classes ≥769px).
  const [mobileDetail, setMobileDetail] = useState(false);
  if (!user) return null;
  const p = user.prefs;
  // Same overload shape as getPref: string defaults widen to `string` so
  // comparisons against other option values don't trip TS2367.
  const g: { (path: string, fb: string): string; <T>(path: string, fb: T): T } =
    (path: string, fb: any) => getPref(p, path, fb);

  return (
    <div className="fx-settings-backdrop" onClick={onClose}>
      <div className={`fx-settings${mobileDetail ? ' is-detail' : ' is-list'}`} onClick={(e) => e.stopPropagation()}>
        <div className="fx-set-nav">
          <div className="fx-set-nav-head">
            <span>Settings</span>
            <button type="button" className="fx-set-close fx-set-close--list" onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div className="fx-set-nav-items">
            {CATS.map((c) => (
              <button
                type="button"
                key={c.key}
                className={`fx-set-navitem${cat === c.key ? ' is-active' : ''}`}
                onClick={() => { setCat(c.key); setMobileDetail(true); }}
              >
                <Icon name={c.icon} size={16} /> <span className="fx-set-navitem-lb">{c.label}</span>
                <span className="fx-set-navitem-chev" aria-hidden>›</span>
              </button>
            ))}
          </div>
        </div>

        <div className="fx-set-content">
          <div className="fx-set-content-head">
            <button type="button" className="fx-set-back" onClick={() => setMobileDetail(false)} aria-label="Back">‹</button>
            <div className="fx-set-content-title">{CATS.find((c) => c.key === cat)?.label}</div>
            <button type="button" className="fx-set-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div className="fx-set-body">
            {cat === 'notifications' && <NotificationsCat g={g} set={set} />}
            {cat === 'navigation' && <NavigationCat g={g} set={set} />}
            {cat === 'home' && <HomeCat g={g} set={set} />}
            {cat === 'appearance' && <AppearanceCat g={g} set={set} />}
            {cat === 'messages' && <MessagesCat g={g} set={set} />}
            {cat === 'language' && <LanguageCat g={g} set={set} />}
            {cat === 'accessibility' && <AccessibilityCat g={g} set={set} />}
            {cat === 'mark_read' && <MarkReadCat g={g} set={set} />}
            {cat === 'av' && <AvCat g={g} set={set} />}
            {cat === 'privacy' && <PrivacyCat g={g} set={set} />}
            {cat === 'advanced' && <AdvancedCat g={g} set={set} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Controls ──
// String defaults widen to `string` (mirrors getPref's overloads) so category
// components can compare prefs against other option values without TS2367.
type G = { (path: string, fb: string): string; <T>(path: string, fb: T): T };
type S = (path: string, value: any) => void;

function Row({ title, desc, children }: { title: string; desc?: string; children?: React.ReactNode }) {
  return (
    <div className="fx-set-row">
      <div className="fx-set-row-main">
        <div className="fx-set-row-title">{title}</div>
        {desc && <div className="fx-set-row-desc">{desc}</div>}
      </div>
      <div className="fx-set-row-ctrl">{children}</div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fx-set-section">
      <div className="fx-set-label">{label}</div>
      <div className="fx-set-card">{children}</div>
    </div>
  );
}

/** Plays the notification chime on click (a user gesture, so autoplay policy
 *  always allows it) — lets users confirm the sound pipeline works in THEIR
 *  browser and on their output device, separately from notification gating. */
function TestSoundButton() {
  const [label, setLabel] = useState('Test');
  const test = () => {
    try {
      const el = new Audio('/public/sound/notification.ogg');
      applySoundSink(el);
      el.volume = 0.6;
      el.play().then(
        () => { setLabel('Played ✓'); window.setTimeout(() => setLabel('Test'), 2000); },
        () => { setLabel('Blocked by browser'); window.setTimeout(() => setLabel('Test'), 3000); },
      );
    } catch {
      setLabel('Blocked by browser');
      window.setTimeout(() => setLabel('Test'), 3000);
    }
  };
  return (
    <button type="button" className="fx-login-back" style={{ marginRight: 10, fontSize: 12.5, padding: '5px 12px' }} onClick={test}>
      {label}
    </button>
  );
}

/** Which physical device notification sounds play through. With an external
 *  monitor attached, the SYSTEM default is often the monitor's (absent)
 *  speakers — an explicit pick here routes chimes to real speakers. Stored in
 *  localStorage: device ids only mean something in this browser. */
function SoundOutputPicker() {
  const [outs, setOuts] = useState<{ id: string; label: string }[]>([]);
  const [sel, setSel] = useState(soundSinkId());
  useEffect(() => {
    let alive = true;
    navigator.mediaDevices?.enumerateDevices?.().then((ds) => {
      if (!alive) return;
      const o = ds.filter((d) => d.kind === 'audiooutput')
        .map((d, i) => ({ id: d.deviceId, label: d.label || `Speaker ${i + 1}` }))
        .filter((d) => d.id && d.id !== 'default');
      setOuts(o);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const choose = (id: string) => {
    setSel(id);
    try { if (id) localStorage.setItem(SOUND_OUT_KEY, id); else localStorage.removeItem(SOUND_OUT_KEY); } catch { /* ignore */ }
    // Immediate audible confirmation through the newly chosen device.
    try {
      const el = new Audio('/public/sound/notification.ogg');
      const anyEl = el as any;
      el.volume = 0.6;
      if (id && typeof anyEl.setSinkId === 'function') anyEl.setSinkId(id).then(() => el.play()).catch(() => el.play());
      else el.play();
    } catch { /* ignore */ }
  };
  if (typeof (HTMLMediaElement.prototype as any).setSinkId !== 'function') return null; // browser can't route output
  return (
    <select className="fx-call-set-sel" style={{ maxWidth: 220, marginRight: 10 }} value={sel} onChange={(e) => choose(e.target.value)}>
      <option value="">System default</option>
      {outs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" className={`fx-switch${on ? ' is-on' : ''}`} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="fx-switch-knob" />
    </button>
  );
}

function Sel({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { v: string; l: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <Select value={value} onChange={onChange} style={{ minWidth: 150 }}
      options={options.map((o) => ({ value: o.v, label: o.l }))} />
  );
}

function RadioList({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { v: string; l: string; d?: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="fx-radiolist">
      {options.map((o) => (
        <label key={o.v} className={`fx-radio${value === o.v ? ' is-sel' : ''}`}>
          <input type="radio" checked={value === o.v} onChange={() => onChange(o.v)} />
          <span className="fx-radio-dot" />
          <span className="fx-radio-main">
            <span className="fx-radio-l">{o.l}</span>
            {o.d && <span className="fx-radio-d">{o.d}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

function TextIn({
  value,
  placeholder,
  onCommit,
  wide,
}: {
  value: string;
  placeholder?: string;
  onCommit: (v: string) => void;
  wide?: boolean;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input
      className="fx-text-input"
      style={wide ? { minWidth: 260 } : undefined}
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== value && onCommit(v)}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

// ── Categories ──

// Device-level push toggle — not a stored pref; it reflects/controls this browser's
// actual Web Push subscription (permission + service worker + backend row).
function PushRow() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pushState().then(setState).catch(() => setState('unsupported'));
  }, []);

  const on = state === 'on';
  const blocked =
    busy || state === null || state === 'unsupported' || state === 'unconfigured' || state === 'denied';

  const toggle = async () => {
    if (blocked && !on) return;
    setBusy(true);
    try {
      setState(on ? await disablePush() : await enablePush());
    } finally {
      setBusy(false);
    }
  };

  const desc =
    state === 'unsupported'
      ? 'This browser can’t do push. On iPhone, install the app to your Home Screen first, then enable it here.'
      : state === 'unconfigured'
        ? 'Push isn’t set up on the server yet (no VAPID key).'
        : state === 'denied'
          ? 'Notifications are blocked for this site — re-enable them in your browser, then toggle this on.'
          : on
            ? 'On for this device — you’ll be notified even when the app is closed.'
            : 'Get notified on this device even when the app is closed. Asks your permission.';

  return (
    <Section label="Push notifications">
      <Row title="Push on this device" desc={desc}>
        <Toggle on={on} onChange={() => toggle()} />
      </Row>
    </Section>
  );
}

function NotificationsCat({ g, set }: { g: G; set: S }) {
  return (
    <>
      <PushRow />
      <Section label="Notify me about…">
        <div className="fx-set-row">
          <RadioList
            value={g('notifications.notify_about', 'dms_mentions')}
            onChange={(v) => set('notifications.notify_about', v)}
            options={[
              { v: 'all', l: 'All new messages', d: 'Every message in your channels and DMs' },
              { v: 'dms_mentions', l: 'Direct messages, mentions & keywords', d: 'The default' },
              { v: 'nothing', l: 'Nothing', d: 'You’ll still see badges in the app' },
            ]}
          />
        </div>
        <Row title="My keywords" desc="Comma-separated words that count as a mention.">
          <TextIn
            wide
            value={g('notifications.keywords', '')}
            placeholder="deploy, prod, urgent"
            onCommit={(v) => set('notifications.keywords', v)}
          />
        </Row>
        <Row
          title="Per-channel & per-person"
          desc="Set a channel’s level from its 🔔 menu (All / Mentions / Nothing). Mark anyone “Always notify me” from their ⋮ menu so they break through mentions-only and mutes."
        >
          <span className="fx-set-hint">Managed inline</span>
        </Row>
      </Section>

      <Section label="Notification schedule">
        <Row title="Only notify me during a schedule" desc="Outside these hours, alerts stay quiet.">
          <Toggle on={g('notifications.schedule_enabled', false)} onChange={(v) => set('notifications.schedule_enabled', v)} />
        </Row>
        {g('notifications.schedule_enabled', false) && (
          <>
            <Row title="Days">
              <Sel
                value={g('notifications.schedule_days', 'weekdays')}
                onChange={(v) => set('notifications.schedule_days', v)}
                options={[
                  { v: 'every_day', l: 'Every day' },
                  { v: 'weekdays', l: 'Weekdays' },
                ]}
              />
            </Row>
            <Row title="Hours">
              <DateTimePicker mode="time" style={{ minWidth: 96 }}
                value={g('notifications.schedule_start', '09:00')}
                onChange={(v) => set('notifications.schedule_start', v)} />
              <span className="fx-acct-value">to</span>
              <DateTimePicker mode="time" style={{ minWidth: 96 }}
                value={g('notifications.schedule_end', '18:00')}
                onChange={(v) => set('notifications.schedule_end', v)} />
            </Row>
          </>
        )}
      </Section>

      <Section label="Sound & appearance">
        <Row title="Play a sound for new messages" desc="Use Test to check your browser allows it — some privacy browsers block sounds until you've clicked in the app.">
          <TestSoundButton />
          <Toggle on={g('notifications.sound_message', true)} onChange={(v) => set('notifications.sound_message', v)} />
        </Row>
        <Row title="Play sounds through" desc="Pick your laptop speakers if the system default is a monitor with no speakers. Changing it plays the chime on that device.">
          <SoundOutputPicker />
        </Row>
        <Row title="Play a sound when a huddle starts">
          <Toggle on={g('notifications.sound_huddle', true)} onChange={(v) => set('notifications.sound_huddle', v)} />
        </Row>
        <Row title="Mute all sounds" desc="Overrides the two switches above.">
          <Toggle on={g('notifications.mute_all_sounds', false)} onChange={(v) => set('notifications.mute_all_sounds', v)} />
        </Row>
        <Row title="Show a badge in the tab title" desc="(1) Helm when something is unread.">
          <Toggle on={g('notifications.show_badge', true)} onChange={(v) => set('notifications.show_badge', v)} />
        </Row>
        <Row title="Flash the window" desc="When a notification arrives.">
          <Sel
            value={g('notifications.flash_window', 'when_hidden')}
            onChange={(v) => set('notifications.flash_window', v)}
            options={[
              { v: 'always', l: 'Always' },
              { v: 'when_hidden', l: 'When hidden' },
              { v: 'never', l: 'Never' },
            ]}
          />
        </Row>
        <Row title="Include a preview of the message" desc="Turn off to hide message text in alerts.">
          <Toggle on={g('notifications.include_preview', true)} onChange={(v) => set('notifications.include_preview', v)} />
        </Row>
      </Section>
    </>
  );
}

function NavigationCat({ g, set }: { g: G; set: S }) {
  return (
    <>
      <Section label="Show in the navigation rail">
        <Row title="Home"><Toggle on={g('navigation.show_home', true)} onChange={(v) => set('navigation.show_home', v)} /></Row>
        <Row title="DMs"><Toggle on={g('navigation.show_dms', true)} onChange={(v) => set('navigation.show_dms', v)} /></Row>
        <Row title="Activity"><Toggle on={g('navigation.show_activity', true)} onChange={(v) => set('navigation.show_activity', v)} /></Row>
        <Row title="Files"><Toggle on={g('navigation.show_files', true)} onChange={(v) => set('navigation.show_files', v)} /></Row>
        <Row title="More"><Toggle on={g('navigation.show_more', true)} onChange={(v) => set('navigation.show_more', v)} /></Row>
      </Section>
      <Section label="Badges">
        <Row title="Show unread badges on rail items" desc="Red counters on DMs and Activity.">
          <Toggle on={g('navigation.show_unread_badges', true)} onChange={(v) => set('navigation.show_unread_badges', v)} />
        </Row>
      </Section>
    </>
  );
}

function HomeCat({ g, set }: { g: G; set: S }) {
  return (
    <>
      <Section label="Show conversations">
        <div className="fx-set-row">
          <RadioList
            value={g('home.show_conversations', 'all')}
            onChange={(v) => set('home.show_conversations', v)}
            options={[
              { v: 'recent', l: 'With activity in the last 30 days' },
              { v: 'unreads', l: 'Unreads' },
              { v: 'mentions', l: 'Mentions' },
              { v: 'all', l: 'All' },
            ]}
          />
        </div>
      </Section>
      <Section label="Sort conversations by">
        <div className="fx-set-row">
          <RadioList
            value={g('home.sort', 'alpha')}
            onChange={(v) => set('home.sort', v)}
            options={[
              { v: 'alpha', l: 'A–Z' },
              { v: 'recent', l: 'Most recent' },
              { v: 'mixed', l: 'Channels A–Z, DMs by recent activity' },
            ]}
          />
        </div>
      </Section>
      <Section label="Sidebar organization">
        <Row title="Show profile photos next to DMs">
          <Toggle on={g('home.show_profile_photos', true)} onChange={(v) => set('home.show_profile_photos', v)} />
        </Row>
        <Row title="Separate private channels" desc="Private channels get their own section.">
          <Toggle on={g('home.separate_private', false)} onChange={(v) => set('home.separate_private', v)} />
        </Row>
        <Row title="Separate agents & DMs from channels" desc="Agents get their own section at the bottom (off lists them with DMs).">
          <Toggle on={g('home.separate_dms', true)} onChange={(v) => set('home.separate_dms', v)} />
        </Row>
        <Row title="Move unread mentions to the top of each section" desc="Conversations mentioning you rise to the top.">
          <Toggle on={g('home.mentions_top', true)} onChange={(v) => set('home.mentions_top', v)} />
        </Row>
        <Row title="Move active people to the top of the DM section">
          <Toggle on={g('home.online_dms_top', true)} onChange={(v) => set('home.online_dms_top', v)} />
        </Row>
        <Row title="Hide muted channels from the sidebar" desc="They stay reachable from Browse channels and pop back on a mention.">
          <Toggle on={g('home.hide_muted', true)} onChange={(v) => set('home.hide_muted', v)} />
        </Row>
      </Section>
    </>
  );
}

function AppearanceCat({ g, set }: { g: G; set: S }) {
  const mode = g('appearance.color_mode', 'dark');
  const theme = g('appearance.theme_name', 'ochre');
  return (
    <>
      <Section label="Color mode">
        <div className="fx-set-row">
          <div className="fx-seg">
            {(['dark', 'light', 'system'] as const).map((m) => (
              <button
                type="button"
                key={m}
                className={`fx-seg-opt${mode === m ? ' is-sel' : ''}`}
                onClick={() => set('appearance.color_mode', m)}
              >
                {m === 'dark' ? 'Dark' : m === 'light' ? 'Light' : 'System'}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section label="Theme — new themes just dropped 🎷">
        <div className="fx-theme-grid">
          {THEMES.map((t) => (
            <button
              type="button"
              key={t}
              className={`fx-theme-swatch is-${t}${theme === t ? ' is-sel' : ''}`}
              onClick={() => set('appearance.theme_name', t)}
            >
              <span className="fx-theme-dot" />
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </Section>

      <Section label="Dock">
        <div className="fx-set-row">
          <div className="fx-set-row-main">
            <div className="fx-set-row-title">Dock position</div>
            <div className="fx-set-row-desc">Put the magnifying app dock on the left rail or along the bottom.</div>
          </div>
          <div className="fx-set-row-ctrl">
            <div className="fx-seg">
              {(['left', 'bottom'] as const).map((d) => (
                <button
                  type="button"
                  key={d}
                  className={`fx-seg-opt${g('appearance.dock_position', 'left') === d ? ' is-sel' : ''}`}
                  onClick={() => set('appearance.dock_position', d)}
                >
                  {d === 'left' ? 'Left rail' : 'Bottom'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section label="Display">
        <Row title="Compact mode" desc="Tighter message spacing, smaller avatars.">
          <Toggle on={g('appearance.compact', false)} onChange={(v) => set('appearance.compact', v)} />
        </Row>
        <Row title="Always show message timestamps">
          <Toggle on={g('appearance.show_timestamps_always', false)} onChange={(v) => set('appearance.show_timestamps_always', v)} />
        </Row>
        <Row title="Emoji skin tone">
          <Sel
            value={String(g('appearance.emoji_skin_tone', 1))}
            onChange={(v) => set('appearance.emoji_skin_tone', Number(v))}
            options={[
              { v: '1', l: '✋ Default' },
              { v: '2', l: '✋🏻 Light' },
              { v: '3', l: '✋🏼 Medium-light' },
              { v: '4', l: '✋🏽 Medium' },
              { v: '5', l: '✋🏾 Medium-dark' },
              { v: '6', l: '✋🏿 Dark' },
            ]}
          />
        </Row>
      </Section>
    </>
  );
}

const SKIN_TONES = [
  { v: 1, e: '✋', l: 'Default' },
  { v: 2, e: '✋🏻', l: 'Light' },
  { v: 3, e: '✋🏼', l: 'Medium-light' },
  { v: 4, e: '✋🏽', l: 'Medium' },
  { v: 5, e: '✋🏾', l: 'Medium-dark' },
  { v: 6, e: '✋🏿', l: 'Dark' },
];

function MessagesCat({ g, set }: { g: G; set: S }) {
  const { user } = useAuth();
  const name = user?.name || user?.email?.split('@')[0] || 'You';
  const compact = g('appearance.compact', false);
  const nameMode = g('messages.names', 'full');
  const shownName = nameMode === 'display' ? name.split(' ')[0] : name;
  const tone = g('appearance.emoji_skin_tone', 1);
  const src = g('messages.quick_reaction_source', 'custom');
  const custom = g<string[]>('messages.quick_reactions', ['✅', '👀', '🙌']) || [];

  return (
    <>
      <Section label="Names">
        <div className="fx-set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <RadioList
            value={nameMode}
            onChange={(v) => set('messages.names', v)}
            options={[
              { v: 'full', l: 'Full name' },
              { v: 'display', l: 'Just first names' },
            ]}
          />
          <div className="fx-preview">
            <div className="fx-preview-lbl">Here’s an example</div>
            <div className={`fx-msg${compact ? ' fx-preview-compact' : ''}`} style={{ padding: '6px 4px' }}>
              <div className="fx-avatar">{(shownName[0] || 'Y').toUpperCase()}</div>
              <div className="fx-msg-body">
                <div className="fx-msg-head"><span className="fx-msg-name">{shownName}</span><span className="fx-msg-time">9:41 AM</span></div>
                <div className="fx-msg-text">👋 Hi, I’m {shownName}</div>
              </div>
            </div>
          </div>
          <div className="fx-set-row-desc">To change your name, open your profile from the account menu.</div>
        </div>
      </Section>

      <Section label="Additional options">
        <Row title="Show who is typing" desc="The “X is typing…” line under a conversation.">
          <Toggle on={g('messages.show_typing', true)} onChange={(v) => set('messages.show_typing', v)} />
        </Row>
        <Row title="24-hour clock" desc="Show message times as 17:30 instead of 5:30 PM.">
          <Toggle on={g('language.clock_24h', false)} onChange={(v) => set('language.clock_24h', v)} />
        </Row>
        <Row title="Color swatches next to hex codes" desc="A little chip next to values like #f7b500 in messages.">
          <Toggle on={g('messages.color_swatches', true)} onChange={(v) => set('messages.color_swatches', v)} />
        </Row>
      </Section>

      <Section label="Emoji">
        <div className="fx-set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <div className="fx-set-row-title">Default skin tone</div>
          <div className="fx-set-row-desc">Used for hand and person emoji in reactions and messages.</div>
          <div className="fx-skin-row">
            {SKIN_TONES.map((t) => (
              <button
                type="button"
                key={t.v}
                className={`fx-skin${tone === t.v ? ' is-sel' : ''}`}
                title={t.l}
                onClick={() => set('appearance.emoji_skin_tone', t.v)}
              >
                {t.e}
              </button>
            ))}
          </div>
        </div>
        <Row title="Show emoji as plain text" desc="Leaves :shortcodes: as text and turns off jumbo sizing.">
          <Toggle on={g('messages.emoji_plain', false)} onChange={(v) => set('messages.emoji_plain', v)} />
        </Row>
        <Row title="Jumbomoji" desc="Emoji-only messages render extra large.">
          <Toggle on={g('messages.jumbomoji', true)} onChange={(v) => set('messages.jumbomoji', v)} />
        </Row>
        <Row title="Convert emoticons to emoji" desc=":) becomes 🙂 as you send.">
          <Toggle on={g('messages.convert_emoticons', true)} onChange={(v) => set('messages.convert_emoticons', v)} />
        </Row>
      </Section>

      <Section label="One-click reactions">
        <Row title="Show one-click reactions on messages" desc="Quick-react buttons when you hover a message.">
          <Toggle on={g('messages.one_click_reactions', true)} onChange={(v) => set('messages.one_click_reactions', v)} />
        </Row>
        {g('messages.one_click_reactions', true) && (
          <div className="fx-set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
            <RadioList
              value={src}
              onChange={(v) => set('messages.quick_reaction_source', v)}
              options={[
                { v: 'frequent', l: 'My most frequently used emoji' },
                { v: 'custom', l: 'Custom', d: 'Pick up to three below.' },
              ]}
            />
            {src === 'custom' && (
              <div className="fx-skin-row">
                {[0, 1, 2].map((i) => (
                  <input
                    key={i}
                    className="fx-text-input fx-quickpick"
                    value={custom[i] || ''}
                    maxLength={4}
                    placeholder="🙂"
                    onChange={(e) => {
                      const next = [...custom];
                      next[i] = e.target.value.trim();
                      set('messages.quick_reactions', next.filter(Boolean).slice(0, 3));
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      <Section label="Inline media & links">
        <Row title="Show images and files uploaded here" desc="Off shows compact file links instead.">
          <Toggle on={g('messages.inline_media', true)} onChange={(v) => set('messages.inline_media', v)} />
        </Row>
        <Row title="Show images linked from the web" desc="Inline an image when a message links straight to one.">
          <Toggle on={g('messages.link_media', true)} onChange={(v) => set('messages.link_media', v)} />
        </Row>
        {g('messages.link_media', true) && (
          <Row title="…even if they’re large" desc="Saved now — a size guard applies once link unfurling ships.">
            <Toggle on={g('messages.link_media_large', false)} onChange={(v) => set('messages.link_media_large', v)} />
          </Row>
        )}
        <Row title="Show text previews of linked sites" desc="Saved now — applies once link unfurling ships.">
          <Toggle on={g('messages.text_previews', true)} onChange={(v) => set('messages.text_previews', v)} />
        </Row>
      </Section>

      <Section label="Bring emails into FxM">
        <div className="fx-set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div className="fx-set-row-desc">
            Forward any email to your personal address and it lands as a message you can keep private or share.
          </div>
          <ForwardingAddress />
        </div>
      </Section>
    </>
  );
}

function ForwardingAddress() {
  const { user } = useAuth();
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const addr = forwardingAddress(user?.id);
  if (!shown) {
    return (
      <button type="button" className="fx-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setShown(true)}>
        Get a forwarding address
      </button>
    );
  }
  return (
    <div className="fx-fwd">
      <code className="fx-fwd-addr">{addr}</code>
      <button
        type="button"
        className="fx-btn"
        onClick={() => { navigator.clipboard?.writeText(addr).then(() => setCopied(true)).catch(() => {}); }}
      >
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
      <div className="fx-set-row-desc" style={{ width: '100%' }}>
        Your address is reserved. Inbound email delivery is on the roadmap.
      </div>
    </div>
  );
}

function LanguageCat({ g, set }: { g: G; set: S }) {
  const auto = g('language.auto_timezone', true);
  const tzs: string[] = (Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone') : ['UTC'];
  return (
    <>
      <Section label="Language">
        <Row title="Language" desc="UI copy is English for now — your choice is saved.">
          <Sel
            value={g('language.language', 'en-US')}
            onChange={(v) => set('language.language', v)}
            options={[
              { v: 'en-US', l: 'English (US)' },
              { v: 'en-GB', l: 'English (UK)' },
              { v: 'hi-IN', l: 'हिन्दी' },
              { v: 'de-DE', l: 'Deutsch' },
              { v: 'fr-FR', l: 'Français' },
              { v: 'es-ES', l: 'Español' },
            ]}
          />
        </Row>
        <Row title="Spellcheck in the composer">
          <Toggle on={g('language.spellcheck', true)} onChange={(v) => set('language.spellcheck', v)} />
        </Row>
      </Section>
      <Section label="Time zone">
        <Row title="Set time zone automatically">
          <Toggle on={auto} onChange={(v) => set('language.auto_timezone', v)} />
        </Row>
        {!auto && (
          <Row title="Time zone">
            <Sel
              value={g('language.timezone', '') || Intl.DateTimeFormat().resolvedOptions().timeZone}
              onChange={(v) => set('language.timezone', v)}
              options={tzs.map((t: string) => ({ v: t, l: t }))}
            />
          </Row>
        )}
        <Row title="24-hour clock">
          <Toggle on={g('language.clock_24h', false)} onChange={(v) => set('language.clock_24h', v)} />
        </Row>
      </Section>
    </>
  );
}

const VERBOSITY_PARTS: { key: string; label: string; desc?: string }[] = [
  { key: 'image_alt', label: 'Image descriptions', desc: 'Alt text on image attachments.' },
  { key: 'datetime', label: 'Date and time' },
  { key: 'reaction_count', label: 'Reaction count' },
  { key: 'attachment_count', label: 'Attachment count', desc: 'Images, files, and voice notes on the message.' },
  { key: 'link_count', label: 'Link count' },
  { key: 'edited', label: 'Edited', desc: 'Whether the message was edited after sending.' },
];

function AccessibilityCat({ g, set }: { g: G; set: S }) {
  const zoom = g('accessibility.zoom', 100);

  const speakPreview = () => {
    const parts: string[] = ['Arjun Desai', 'Shipping the new build today 🚀'];
    if (g('accessibility.verbosity.edited', true)) parts.push('edited');
    if (g('accessibility.verbosity.image_alt', true)) parts.push('image: the deploy dashboard, all green');
    if (g('accessibility.verbosity.attachment_count', true)) parts.push('1 attachment');
    if (g('accessibility.verbosity.reaction_count', true)) parts.push('3 reactions');
    if (g('accessibility.verbosity.link_count', true)) parts.push('1 link');
    if (g('accessibility.verbosity.datetime', true)) parts.push('today at 9:41 AM');
    try {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(parts.join(', ')));
    } catch {
      window.alert(parts.join(', '));
    }
  };

  return (
    <>
      <Section label="Display">
        <Row title="Zoom" desc="Scales the whole app. Applies instantly.">
          <Sel
            value={String([80, 90, 100, 110, 125, 150].includes(zoom) ? zoom : 100)}
            onChange={(v) => set('accessibility.zoom', Number(v))}
            options={[
              { v: '80', l: '80%' },
              { v: '90', l: '90%' },
              { v: '100', l: '100% (default)' },
              { v: '110', l: '110%' },
              { v: '125', l: '125%' },
              { v: '150', l: '150%' },
            ]}
          />
        </Row>
      </Section>

      <Section label="Alt text reminders">
        <Row title="Remind me to describe images" desc="Prompt for a description whenever you upload an image.">
          <Toggle on={g('accessibility.alt_text_reminder', false)} onChange={(v) => set('accessibility.alt_text_reminder', v)} />
        </Row>
      </Section>

      <Section label="Other display options">
        <Row title="Show names when hovering the dock" desc="The floating labels next to dock icons.">
          <Toggle on={g('accessibility.dock_tooltips', true)} onChange={(v) => set('accessibility.dock_tooltips', v)} />
        </Row>
        <Row title="Underline links">
          <Toggle on={g('accessibility.underline_links', false)} onChange={(v) => set('accessibility.underline_links', v)} />
        </Row>
        <Row title="Allow animations" desc="Off disables dock magnification, the roller, and transitions.">
          <Toggle on={g('accessibility.animations', true)} onChange={(v) => set('accessibility.animations', v)} />
        </Row>
      </Section>

      <Section label="Keyboard">
        <div className="fx-set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div className="fx-set-row-title">
            Press <span className="fx-kbd">↑</span> in the empty message field to:
          </div>
          <RadioList
            value={g('accessibility.up_arrow', 'edit_last')}
            onChange={(v) => set('accessibility.up_arrow', v)}
            options={[
              { v: 'focus_list', l: 'Move focus to the message list', d: 'The newest message is highlighted' },
              { v: 'focus_list_when_empty', l: 'Move focus to the list, only if the field is empty' },
              { v: 'edit_last', l: 'Edit your last message', d: 'Your last message opens in the composer' },
            ]}
          />
          <div className="fx-set-row-desc">
            You can always press <span className="fx-kbd">Ctrl</span> + <span className="fx-kbd">↑</span> to edit your
            last message.
          </div>
        </div>
      </Section>

      <Section label="Screen reader — message verbosity">
        <div className="fx-set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
          <div className="fx-set-row-desc">
            Choose which parts of a message are announced. Applied as the accessible label on every message.
          </div>
          <label className="fx-verb-row is-locked">
            <input type="checkbox" checked disabled /> Sender
            <span className="fx-verb-note">Always announced.</span>
          </label>
          <label className="fx-verb-row is-locked">
            <input type="checkbox" checked disabled /> Message
            <span className="fx-verb-note">Always announced.</span>
          </label>
          {VERBOSITY_PARTS.map((pt) => (
            <label className="fx-verb-row" key={pt.key}>
              <input
                type="checkbox"
                checked={g(`accessibility.verbosity.${pt.key}`, true)}
                onChange={(e) => set(`accessibility.verbosity.${pt.key}`, e.target.checked)}
              />
              {pt.label}
              {pt.desc && <span className="fx-verb-note">{pt.desc}</span>}
            </label>
          ))}
          <div className="fx-modal-actions" style={{ paddingTop: 6 }}>
            <button type="button" className="fx-btn" onClick={speakPreview}>▷ Play preview</button>
            <button
              type="button"
              className="fx-btn"
              onClick={() =>
                set('accessibility.verbosity', {
                  image_alt: true, datetime: true, reaction_count: true,
                  attachment_count: true, link_count: true, edited: true,
                })
              }
            >
              Reset to default
            </button>
          </div>
        </div>
      </Section>
    </>
  );
}

function MarkReadCat({ g, set }: { g: G; set: S }) {
  return (
    <>
      <div className="fx-set-intro">
        FxM marks a conversation read as soon as you view it. Change that here.
      </div>
      <Section label="When I view a channel">
        <div className="fx-set-row">
          <RadioList
            value={g('mark_read.mode', 'left_off_mark')}
            onChange={(v) => set('mark_read.mode', v)}
            options={[
              { v: 'left_off_mark', l: 'Start me where I left off, and mark the channel read', d: 'Scrolls to the “New” divider' },
              { v: 'newest_mark', l: 'Start me at the newest message, and mark the channel read' },
              { v: 'newest_unread', l: 'Start me at the newest message, but leave unseen messages unread' },
            ]}
          />
        </div>
      </Section>
      <Section label="When I mark everything as read">
        <Row title="Prompt to confirm" desc="Ask before clearing every unread in the workspace.">
          <Toggle on={g('mark_read.confirm_all', true)} onChange={(v) => set('mark_read.confirm_all', v)} />
        </Row>
      </Section>
      <Section label="Keyboard shortcuts">
        <Row title="Mark the current conversation as read"><span className="fx-kbd">Esc</span></Row>
        <Row title="Mark everything as read"><span className="fx-kbd">Shift</span><span className="fx-kbd">Esc</span></Row>
        <Row title="Mark a message as unread" desc="Everything from that message on goes back to unread.">
          <span className="fx-kbd">Alt</span><span className="fx-acct-value">+ click message</span>
        </Row>
        <Row title="See every shortcut"><span className="fx-kbd">Ctrl</span><span className="fx-kbd">/</span></Row>
      </Section>
    </>
  );
}

function AvCat({ g, set }: { g: G; set: S }) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    navigator.mediaDevices
      ?.enumerateDevices()
      .then(setDevices)
      .catch(() => setErr('Could not list devices — grant mic/camera permission first.'));
  }, []);
  const opts = (kind: MediaDeviceKind) => [
    { v: '', l: 'System default' },
    ...devices
      .filter((d) => d.kind === kind && d.deviceId)
      .map((d) => ({ v: d.deviceId, l: d.label || `${kind} ${d.deviceId.slice(0, 6)}` })),
  ];
  return (
    <>
      <Section label="Devices">
        <Row title="Microphone">
          <Sel value={g('av.mic_id', '')} onChange={(v) => set('av.mic_id', v)} options={opts('audioinput')} />
        </Row>
        <Row title="Speaker">
          <Sel value={g('av.speaker_id', '')} onChange={(v) => set('av.speaker_id', v)} options={opts('audiooutput')} />
        </Row>
        <Row title="Camera">
          <Sel value={g('av.camera_id', '')} onChange={(v) => set('av.camera_id', v)} options={opts('videoinput')} />
        </Row>
        {err && <div className="fx-set-row"><span className="fx-set-row-desc">{err}</span></div>}
      </Section>
      <Section label="When joining a call…">
        <Row title="Mute my microphone">
          <Toggle on={g('av.join_muted', false)} onChange={(v) => set('av.join_muted', v)} />
        </Row>
        <Row title="Join with my camera off">
          <Toggle on={g('av.join_camera_off', false)} onChange={(v) => set('av.join_camera_off', v)} />
        </Row>
        <Row title="Turn on captions automatically" desc="Saved now — captions light up with the transcription service.">
          <Toggle on={g('av.captions', false)} onChange={(v) => set('av.captions', v)} />
        </Row>
        <Row
          title="Warn me before calling a big channel"
          desc={`Confirm before starting a call in a channel with more than ${g('av.large_channel_threshold', 150)} members.`}
        >
          <Toggle on={g('av.warn_large_channel', true)} onChange={(v) => set('av.warn_large_channel', v)} />
        </Row>
        <Row title="Blur my video background" desc="Saved now — applies when background effects ship in calls.">
          <Toggle on={g('av.blur_background', false)} onChange={(v) => { set('av.blur_background', v); try { localStorage.removeItem('fxmchat_call_bg'); } catch { /* ignore */ } }} />
        </Row>
        <Row title="Show an audio and video preview" desc="Check your camera and mic before you join.">
          <Toggle on={g('av.show_preview', true)} onChange={(v) => set('av.show_preview', v)} />
        </Row>
        <Row title="Noise suppression" desc="Browser-level noise suppression on your mic.">
          <Toggle on={g('av.noise_suppression', true)} onChange={(v) => set('av.noise_suppression', v)} />
        </Row>
      </Section>
      <Section label="When FxM is in the background">
        <Row
          title="Keyboard shortcut to mute"
          desc="While you're on a call, toggle your mic from anywhere in the app."
        >
          <span className="fx-kbd">Ctrl</span><span className="fx-kbd">Shift</span><span className="fx-kbd">Space</span>
          <Toggle on={g('av.bg_mute_shortcut', true)} onChange={(v) => set('av.bg_mute_shortcut', v)} />
        </Row>
      </Section>
      <Section label="When your screen locks…">
        <Row
          title="Automatically leave calls"
          desc="Uses the browser's idle detection where available (you may be asked for permission)."
        >
          <Toggle
            on={g('av.leave_on_lock', true)}
            onChange={async (v) => {
              set('av.leave_on_lock', v);
              if (v && 'IdleDetector' in window) {
                try { await (window as any).IdleDetector.requestPermission(); } catch { /* optional */ }
              }
            }}
          />
        </Row>
      </Section>
      <Section label="Troubleshooting">
        <Row title="Run a device check" desc="Quickly confirm your mic and camera are reachable.">
          <DeviceCheck />
        </Row>
        <Row title="Reset call settings" desc="Back to defaults: devices, joining, music, shortcuts.">
          <button
            type="button"
            className="fx-btn"
            onClick={() =>
              set('av', {
                mic_id: '', speaker_id: '', camera_id: '', join_muted: false, join_camera_off: false,
                noise_suppression: true, captions: false, warn_large_channel: true,
                large_channel_threshold: 150, blur_background: false, show_preview: true,
                bg_mute_shortcut: true, alone_music: true, alone_music_after: '1m', leave_on_lock: true,
              })
            }
          >
            Reset
          </button>
        </Row>
      </Section>
    </>
  );
}

function DeviceCheck() {
  const [state, setState] = useState<'idle' | 'running' | 'ok' | 'fail'>('idle');
  const [detail, setDetail] = useState('');
  const run = async () => {
    setState('running');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      const mics = stream.getAudioTracks().length;
      const cams = stream.getVideoTracks().length;
      stream.getTracks().forEach((t) => t.stop());
      setDetail(`mic ${mics ? '✓' : '✗'} · camera ${cams ? '✓' : '✗'}`);
      setState(mics ? 'ok' : 'fail');
    } catch (e: any) {
      setDetail(e?.name === 'NotAllowedError' ? 'Permission denied' : 'No devices found');
      setState('fail');
    }
  };
  return (
    <>
      {detail && <span className={`fx-acct-value${state === 'fail' ? ' fx-devfail' : ''}`}>{detail}</span>}
      <button type="button" className="fx-btn" onClick={run} disabled={state === 'running'}>
        {state === 'running' ? 'Checking…' : 'Run check'}
      </button>
    </>
  );
}

function PrivacyCat({ g, set }: { g: G; set: S }) {
  return (
    <>
      <Section label="Visibility">
        <Row title="Let others view my profile" desc="When on, any workspace member can open your profile (status, local time, email, custom fields). Off shows only your name and avatar.">
          <Toggle on={g('privacy.profile_visible', true)} onChange={(v) => set('privacy.profile_visible', v)} />
        </Row>
        <Row title="Hide my presence" desc="Others always see you as away.">
          <Toggle on={g('privacy.hide_presence', false)} onChange={(v) => set('privacy.hide_presence', v)} />
        </Row>
        <Row title="Don’t broadcast when I’m typing">
          <Toggle on={g('privacy.hide_typing', false)} onChange={(v) => set('privacy.hide_typing', v)} />
        </Row>
      </Section>
      <Section label="Read receipts">
        <Row title="Send read receipts" desc="When on, people can see “Seen” once you’ve read their message. Turn off to read without letting others know.">
          <Toggle on={g('privacy.read_receipts', true)} onChange={(v) => set('privacy.read_receipts', v)} />
        </Row>
      </Section>
    </>
  );
}

function AdvancedCat({ g, set }: { g: G; set: S }) {
  return (
    <>
      <Section label="Input options">
        <Row title="Press Enter to send" desc="Off makes Enter add a new line (send with the button).">
          <Toggle on={g('advanced.enter_sends', true)} onChange={(v) => set('advanced.enter_sends', v)} />
        </Row>
        <Row title="Format code with ```" desc="Inside an unclosed code block, Enter adds a new line.">
          <Toggle on={g('advanced.code_block_enter', true)} onChange={(v) => set('advanced.code_block_enter', v)} />
        </Row>
      </Section>
      <Section label="Search options">
        <Row title="Exclude these channels from search" desc="Comma-separated channel names.">
          <TextIn
            wide
            value={g('advanced.exclude_channels_search', '')}
            placeholder="noisy-bots, archive"
            onCommit={(v) => set('advanced.exclude_channels_search', v)}
          />
        </Row>
      </Section>
      <Section label="Other">
        <Row title="Warn before uploading external files">
          <Toggle on={g('advanced.confirm_external_files', true)} onChange={(v) => set('advanced.confirm_external_files', v)} />
        </Row>
      </Section>
    </>
  );
}
